import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AggregatedStatus, DeviceRoles, InstallationItemGatewayDevice } from '@vi-iot/client';

import { GatewayStatusWithChangedAt, Platform } from '../installation-details';

import { InstallationsActions } from './installations.actions';
import { InstallationPackageStatus } from './installations.model';
import { InstallationsSelectors } from './installations.selectors';

@Injectable({ providedIn: 'root' })
export class InstallationsStore {
  state$ = this.store.select(InstallationsSelectors.getState());
  installations$ = this.store.select(InstallationsSelectors.selectInstallations());
  deregisterInstallationState$ = this.store.select(
    InstallationsSelectors.getDeregisterInstallationState()
  );
  loaded$ = this.store.select(InstallationsSelectors.getLoadedIndicator());
  isData$ = this.store.select(InstallationsSelectors.getIsDataIndicator());
  areInstallationsAndSubscriptionsLoaded$ = this.store.select(
    InstallationsSelectors.areInstalltionsAndSubscriptionsLoaded
  );
  numberOfDisplayedInstallations$ = this.store.select(
    InstallationsSelectors.selectNumberOfDisplayedInstallations
  );

  constructor(private store: Store) {}

  listInstallations(): void {
    this.store.dispatch(InstallationsActions.listFirstThousandInstallations());
  }

  removeInstallation(installationId: number): void {
    this.store.dispatch(InstallationsActions.removeInstallation({ installationId }));
  }

  deregisterInstallation(installationId: number): void {
    this.store.dispatch(InstallationsActions.deregisterInstallation({ installationId }));
  }

  resetDeregisterState(): void {
    this.store.dispatch(InstallationsActions.resetDeregisterState());
  }

  liveUpdateAggregatedStatus(
    aggregatedStatus: AggregatedStatus,
    lastChangedAt: string,
    gatewaySerial: string
  ): void {
    this.store.dispatch(
      InstallationsActions.liveUpdateAggregatedStatus({
        aggregatedStatus,
        lastChangedAt,
        gatewaySerial,
      })
    );
  }

  selectInstallationPackage(boilerSerial: string): Observable<InstallationPackageStatus> {
    return this.store.select(InstallationsSelectors.getInstallationPackage(boilerSerial));
  }

  selectAggregatedStatus(gatewaySerial: string): Observable<AggregatedStatus> {
    return this.store.select(InstallationsSelectors.getAggregatedStatus(gatewaySerial));
  }

  selectLastStatusChangedAt(gatewaySerial: string): Observable<string> {
    return this.store.select(InstallationsSelectors.getLastStatusChangedAt(gatewaySerial));
  }

  selectGatewayStatus(
    installationId: number,
    gatewaySerial: string
  ): Observable<GatewayStatusWithChangedAt> {
    return this.store.select(
      InstallationsSelectors.getGatewayStatus(installationId, gatewaySerial)
    );
  }

  selectMainDevices(installationId: number): Observable<InstallationItemGatewayDevice[]> {
    return this.store.select(InstallationsSelectors.getInstallationMainDevices(installationId));
  }

  selectMainDevicesAndOrderTiles(
    installationId: number
  ): Observable<InstallationItemGatewayDevice[]> {
    return this.store.select(InstallationsSelectors.selectMainDevicesAndOrderTiles(installationId));
  }

  isStandaloneGateway(gatewaySerial: string): Observable<boolean> {
    return this.store.select(InstallationsSelectors.isStandaloneGateway(gatewaySerial));
  }

  getPlatformTypeWithInstallationId(installationId: number): Observable<Platform> {
    return this.store.select(
      InstallationsSelectors.getPlatformTypeWithInstallationId(installationId)
    );
  }

  selectInstallationSubscriptionDetails(
    boilerSerial: string
  ): Observable<InstallationPackageStatus> {
    return this.store.select(
      InstallationsSelectors.getInstallationSubscriptionDetails(boilerSerial)
    );
  }

  showRemoteInspectionRecommendation(installationId: number): Observable<boolean> {
    return this.store.select(
      InstallationsSelectors.showRemoteInspectionRecommendation(installationId)
    );
  }

  showAlertBeforeTrialPackageEnds(boilerSerial: string): Observable<boolean> {
    return this.store.select(InstallationsSelectors.showAlertBeforeTrialPackageEnds(boilerSerial));
  }

  showAlertBeforeContingentPackageEnds(boilerSerial: string): Observable<boolean> {
    return this.store.select(
      InstallationsSelectors.showAlertBeforeContingentPackageEnds(boilerSerial)
    );
  }

  getActivePackageExpirationDate(boilerSerial: string): Observable<Date> {
    return this.store.select(InstallationsSelectors.getActivePackageExpirationDate(boilerSerial));
  }

  getAllDeviceSerialsByAnyMatchingRole(roles: DeviceRoles[]): Observable<string[]> {
    return this.store.select(InstallationsSelectors.getAllDeviceSerialsByAnyMatchingRole(roles));
  }

  fetchNumberOfDisplayedInstallations(): void {
    this.store.dispatch(InstallationsActions.fetchNumberOfDisplayedInstallations.attempt());
  }

  setNumberOfDisplayedInstallations(numberOfInstallationsToDisplay: number): void {
    this.store.dispatch(
      InstallationsActions.setNumberOfDisplayedInstallations.attempt({
        numberOfInstallationsToDisplay,
      })
    );
  }
}
